/**
 * The two field widgets the remaining kind editors need that no other surface
 * had, plus the small helpers and the stylesheet they share.
 *
 * It is deliberately thin. `policy-pattern-list`, `policy-typed-value`,
 * `policy-selector`, `policy-envelope` and `policy-findings` already exist and are
 * shared; what was missing was a **duration** field over Sluice's own wire format
 * and a **secret reference** picker over the instance's materialised inventory.
 * Both are policy-domain controls assembled from the `bn-*` kit rather than
 * additions to it.
 *
 * Implements `SC-F518`'s duration handling and the `secret://` discipline of
 * `SC-F326` / `SC-F521` / linter rules `L14`, `L15` and `L31`.
 */

import { LitElement, css, html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../../components/index.js";
import type {
  BnDiagnosticSeverity,
  BnSelectChangeDetail,
  BnSelectOption,
} from "../../components/index.js";

import { ApiError, parseError } from "../../api/errors.js";
import { listSecrets, type SecretEntry } from "../../api/instances.js";
import type { PolicyFinding } from "../../api/policies.js";

import type { BindingSummary } from "./bindings.js";
import type { Condition, Selector } from "./model.js";
import "./policy-conditions.js";
import type { ConditionsChangeDetail } from "./policy-conditions.js";
import { diagnosticSeverity, findingsAt, worst } from "./policy-findings.js";
import "./policy-selector.js";
import type { SchemaCache } from "./schema-cache.js";
import type { PolicySchema, SchemaField } from "./schema.js";

/* ── The shared stylesheet ─────────────────────────────────────────────── */

/**
 * kindFormStyles is the layout the remaining kind editors adopt, so a mask form
 * and an approval form are visibly the same product as the two editors that
 * shipped first. Composed as `static styles = [kindFormStyles, css`…`]`; the
 * `--bino-*` custom properties pierce every one of these shadow roots from
 * `:root`, so nothing here branches on the theme.
 */
export const kindFormStyles = css`
  :host {
    display: block;
    font-family: var(--bino-font-sans);
    color: var(--bino-text);
    font-size: var(--bino-font-size-md);
  }
  section {
    margin-bottom: var(--bino-space-xl);
  }
  section:last-of-type {
    margin-bottom: 0;
  }
  h3 {
    margin: 0 0 var(--bino-space-xs);
    font-size: var(--bino-font-size-md);
    font-weight: 600;
  }
  .lede {
    margin: 0 0 var(--bino-space-md);
    font-size: var(--bino-font-size-sm);
    color: var(--bino-text-secondary);
    max-width: 80ch;
  }
  p {
    margin: 0 0 var(--bino-space-sm);
    max-width: 80ch;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul {
    margin: 0 0 var(--bino-space-sm);
    padding-left: var(--bino-space-lg);
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--bino-space-md);
  }
  .row > * {
    flex: 1 1 16rem;
    min-width: 0;
  }
  .row.tight {
    gap: var(--bino-space-sm);
    align-items: center;
  }
  .row.tight > * {
    flex: 0 0 auto;
  }
  .stack {
    display: grid;
    gap: var(--bino-space-md);
  }
  .spacer {
    flex: 1 1 auto;
  }
  .sub {
    font-size: var(--bino-font-size-sm);
    color: var(--bino-text-secondary);
    max-width: 80ch;
  }
  code {
    font-family: var(--bino-font-mono);
    font-size: var(--bino-font-size-base);
    overflow-wrap: anywhere;
  }
  .mono {
    font-family: var(--bino-font-mono);
    font-size: var(--bino-font-size-base);
  }

  /* A repeater item. Ordered lists carry their index because order is meaning in
     several kinds: reject rules and classification rules are both read in order. */
  .items {
    display: grid;
    gap: var(--bino-space-md);
    margin: 0 0 var(--bino-space-md);
    padding: 0;
    list-style: none;
  }
  .item {
    border: 1px solid var(--bino-border);
    border-radius: var(--bino-radius);
    background: var(--bino-surface-subtle);
    padding: var(--bino-space-md);
  }
  .item-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--bino-space-sm);
    margin-bottom: var(--bino-space-md);
  }
  .item-title {
    font-weight: 600;
    font-size: var(--bino-font-size-base);
  }

  /* The honesty row: a disabled control plus the badge and the reason. */
  .dead {
    display: grid;
    gap: var(--bino-space-xs);
  }
  .dead-reason {
    font-size: var(--bino-font-size-sm);
    color: var(--bino-warning-text);
    background: var(--bino-warning-bg);
    border: 1px solid var(--bino-warning-border);
    border-radius: var(--bino-radius-sm);
    padding: var(--bino-space-xs) var(--bino-space-sm);
    max-width: 80ch;
  }

  /* A finding that belongs to a block rather than to one control. */
  .alert {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--bino-space-xs);
    margin: var(--bino-space-xs) 0 0;
    font-size: var(--bino-font-size-sm);
    max-width: 80ch;
  }
  .alert.error {
    color: var(--bino-error);
  }
  .alert.warn {
    color: var(--bino-warning);
  }
  .rule {
    font-family: var(--bino-font-mono);
    font-size: var(--bino-font-size-xs);
    padding: 0 var(--bino-space-xs);
    border-radius: var(--bino-radius-sm);
    background: var(--bino-surface-inset);
  }

  table.preview {
    border-collapse: collapse;
    font-size: var(--bino-font-size-base);
  }
  table.preview th,
  table.preview td {
    text-align: left;
    padding: var(--bino-space-xs) var(--bino-space-sm);
    border-bottom: 1px solid var(--bino-border);
  }
  table.preview th {
    font-family: var(--bino-font-mono);
    font-size: var(--bino-font-size-xs);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--bino-text-secondary);
    font-weight: 600;
  }
  table.preview td {
    font-family: var(--bino-font-mono);
  }
  .scroll {
    overflow-x: auto;
  }

  /* bn-code's shell is height:100%, so its HOST has to be given a height or the
     percentage resolves against auto and the editor collapses to nothing. */
  .code {
    display: block;
    height: 7rem;
  }
  .code.tall {
    height: 15rem;
  }
  .code > bn-code {
    display: block;
    height: 100%;
  }
`;

/* ── Helpers ───────────────────────────────────────────────────────────── */

/** The three properties bn-field and bn-input take to render one finding. */
export interface FieldDiagnostic {
  error: string;
  rule: string;
  severity: BnDiagnosticSeverity;
}

const CLEAN: FieldDiagnostic = { error: "", rule: "", severity: "error" };

/**
 * fieldDiag is the worst finding anchored at exactly `path`, as field props.
 *
 * Exact rather than prefix: a finding on `spec.mask.args.pattern` belongs on the
 * pattern input, not on the whole mask block. A block-level finding is
 * {@link renderBlockAlert}'s.
 */
export function fieldDiag(findings: PolicyFinding[], path: string): FieldDiagnostic {
  const f = worst(findingsAt(findings, path));
  if (!f) return CLEAN;
  return { error: f.message, rule: f.rule, severity: diagnosticSeverity(f.severity) };
}

/** localDiag prefers a client-side message over the server's. A local check is
 * about the keystroke and answers now; the linter's finding is about the stored
 * document and arrives on the next save. */
export function localDiag(
  findings: PolicyFinding[],
  path: string,
  local: string,
  severity: BnDiagnosticSeverity = "error",
): FieldDiagnostic {
  if (local !== "") return { error: local, rule: "", severity };
  return fieldDiag(findings, path);
}

/**
 * renderBlockAlert renders a finding that belongs to a group of controls rather
 * than to one input — "at least one rule is required", "the matcher names no
 * column".
 *
 * It is a `role="alert"` paragraph and not a label-less bn-field: a labelled field
 * wrapping nothing is announced as a form control with no purpose, which is worse
 * for a screen reader than a plain live region.
 */
export function renderBlockAlert(
  findings: PolicyFinding[],
  path: string,
): TemplateResult | typeof nothing {
  const f = worst(findingsAt(findings, path));
  if (!f) return nothing;
  const tone = diagnosticSeverity(f.severity) === "error" ? "error" : "warn";
  return html`<p class="alert ${tone}" role="alert">
    ${f.rule ? html`<code class="rule">${f.rule}</code>` : nothing}
    <span>${f.message}</span>
  </p>`;
}

/**
 * renderDeadField renders a field the pinned build carries and does not act on:
 * the control, disabled, with the badge and the reason visible beside it.
 *
 * It is not dropped, and that is the point. A `broken` or `declared-only` field
 * still has a value in an imported policy, and silently removing it makes that
 * policy **broader** — so the reader has to see that the value is there and does
 * nothing (`concept.md` §7 principle 8, `SC-F505`).
 */
export function renderDeadField(
  schema: PolicySchema,
  field: SchemaField | undefined,
  value: string,
  label = "",
): TemplateResult | typeof nothing {
  if (!field) return nothing;
  const badge = schema.badge(field) || "not enforced in this build";
  return html`<bn-field label=${label || humanize(field.name)}>
    <div class="dead">
      <bn-input mono disabled .value=${value}></bn-input>
      <div class="dead-reason">
        <bn-badge variant="not-enforced">${badge}</bn-badge>
        ${schema.disabledReason(field)}
        ${field.source ? html` <code>${field.source}</code>` : nothing}
      </div>
    </div>
  </bn-field>`;
}

/**
 * renderUnavailable names the enum members an editor left out of its dropdown,
 * and why.
 *
 * `03` §3.5 and `SC-S514` / `SC-S520` are explicit that a value the build refuses
 * must be **absent** from the select — `custom` and `external` "do not appear in
 * the dropdown at all", `postgres` and `otlp` "are absent". Absent and
 * *unexplained* is what principle 8 forbids, so this note is the other half of the
 * omission, and it is generated from the schema's own `status` and `reason` so no
 * list of refused values is written down in a template.
 */
export function renderUnavailable(
  schema: PolicySchema,
  enumName: string | undefined,
  eyebrow: string,
): TemplateResult | typeof nothing {
  const gone = (schema.enumOf(enumName)?.values ?? []).filter((v) => {
    const offer = schema.status(v.status).offer;
    return offer === "no" || offer === "hidden";
  });
  if (gone.length === 0) return nothing;
  return html`<bn-panel eyebrow=${eyebrow}>
    <p class="sub">
      These values exist in Sluice's own grammar and are not offered here, because the pinned build
      refuses them:
    </p>
    <ul class="sub">
      ${gone.map(
        (v) => html`<li>
          <code>${v.value}</code> — ${[schema.status(v.status).badge, v.reason]
            .filter(Boolean)
            .join(": ")}
        </li>`,
      )}
    </ul>
  </bn-panel>`;
}

/**
 * renderConstraint quotes one of a kind's declared cross-field constraints
 * verbatim.
 *
 * Verbatim rather than paraphrased, because a paraphrase is a second wording that
 * drifts: the schema is drift-checked against the pinned Sluice source and a
 * sentence retyped into a Lit template is not. `enforcedBy` is shown because
 * "Sluice refuses this" and "Sluice Cloud refuses this" are different facts for an
 * author deciding whether to work around it.
 */
export function renderConstraint(
  schema: PolicySchema,
  kind: string,
  id: string,
  eyebrow: string,
): TemplateResult | typeof nothing {
  const kindSchema = (schema.doc.kinds ?? []).find((k) => k.kind === kind);
  // `source` is present on the constraints the schema document carries and is not
  // in `api/policies.ts`'s narrowing, which stops at the fields every constraint
  // has. Reading it here rather than widening the shared type keeps that type
  // honest about what is guaranteed.
  const c = (kindSchema?.constraints ?? []).find((x) => x.id === id) as
    | { statement: string; enforcedBy: string; linter?: string; source?: string }
    | undefined;
  if (!c) return nothing;
  return html`<bn-panel eyebrow=${eyebrow}>
    <p>${c.statement}</p>
    <p class="sub">
      ${c.enforcedBy === "us"
        ? "Refused by Sluice Cloud before save — Sluice itself accepts it."
        : "Refused by Sluice when the bundle loads."}${c.linter
        ? html` Linter rule <code>${c.linter}</code>.`
        : nothing}${c.source ? html` Source: <code>${c.source}</code>.` : nothing}
    </p>
  </bn-panel>`;
}

/**
 * humanize turns a schema field name into a label: `showFirst` → "Show first".
 *
 * The schema carries a display label for an enum *member* and not for a field, and
 * a hand-written label table here would be a second list to keep in step with the
 * document. So the name is the label, tidied.
 */
export function humanize(name: string): string {
  const spaced = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** asText renders a scalar for a text input; `undefined` and `null` are both "". */
export function asText(v: unknown): string {
  if (v === undefined || v === null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

/** asNumber narrows to a finite number, or undefined. */
export function asNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** asStrings narrows to a string array, dropping non-strings rather than
 * rendering `[object Object]` into a chip. */
export function asStrings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

/** dropAt removes one entry from a repeater. */
export function dropAt<T>(items: readonly T[], index: number): T[] {
  return items.filter((_, i) => i !== index);
}

/** putAt replaces one entry. */
export function putAt<T>(items: readonly T[], index: number, item: T): T[] {
  const out = [...items];
  out[index] = item;
  return out;
}

/** shiftAt moves one entry, clamped at the ends. Repeaters need it because order
 * is meaning in several kinds. */
export function shiftAt<T>(items: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return [...items];
  const out = [...items];
  const [moved] = out.splice(from, 1);
  if (moved === undefined) return [...items];
  out.splice(to, 0, moved);
  return out;
}

/**
 * emptyToUndefined collapses an editor's "cleared" state onto the wire's
 * "absent".
 *
 * This is load-bearing rather than tidy. The renderer omits an absent field, so
 * writing `""` or `[]` where absent is meant changes the rendered bytes — and
 * therefore the object's content digest, which is its identity — for a change the
 * author did not make.
 */
export function emptyToUndefined<T>(value: T[] | string): T[] | string | undefined {
  if (typeof value === "string") return value === "" ? undefined : value;
  return value.length === 0 ? undefined : value;
}

/* ── policy-duration ───────────────────────────────────────────────────── */

/** Detail of policy-duration's `duration-change` event. `value` is the canonical
 * wire string, or `""` for absent. */
export interface DurationChangeDetail {
  value: string;
}

/** The units the wire format accepts. Milliseconds are offered because
 * `time.ParseDuration` accepts them and a sub-second query timeout is a legitimate
 * thing to want. */
const DURATION_UNITS: BnSelectOption[] = [
  { value: "ms", label: "milliseconds" },
  { value: "s", label: "seconds" },
  { value: "m", label: "minutes" },
  { value: "h", label: "hours" },
];

/**
 * `<policy-duration>` is number-plus-unit over Sluice's duration wire format
 * (`pkg/apitypes/duration.go`).
 *
 * The wire accepts three spellings — a `time.ParseDuration` string, a bare integer
 * meaning **seconds**, and a float meaning seconds — and this control always emits
 * the canonical string. That is what keeps the rendered bytes a function of the
 * record rather than of whichever spelling an author happened to type.
 *
 * Zero is the wire's "absent": it marshals as the empty string and the renderer
 * omits it rather than writing `0s`, so the control emits `""` for zero and the
 * key leaves the document.
 *
 * A composite input like `1h30m` round-trips as `90m` — read into the largest unit
 * that divides it exactly and re-emitted from there, so the value survives even
 * though the spelling is normalised.
 *
 * Events: `duration-change` — `CustomEvent<DurationChangeDetail>`, composed.
 */
@customElement("policy-duration")
export class PolicyDuration extends LitElement {
  @property() value = "";
  @property() label = "";
  @property() help = "";
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) required = false;
  /** Smallest acceptable value in milliseconds. Negative durations are rejected
   * upstream, so this is 0 everywhere today. */
  @property({ type: Number }) min = 0;
  @property() diagnostic = "";
  @property() rule = "";
  @property() severity: BnDiagnosticSeverity = "error";

  @state() private local = "";

  static override styles = [
    kindFormStyles,
    css`
      .pair {
        display: flex;
        gap: var(--bino-space-sm);
      }
      .amount {
        flex: 1 1 6rem;
        min-width: 0;
      }
      .unit {
        flex: 0 0 9rem;
      }
    `,
  ];

  /** The stored string split into an amount and a unit for display. */
  private get parts(): { amount: string; unit: string } {
    const raw = this.value.trim();
    if (raw === "") return { amount: "", unit: "s" };
    const ms = durationToMs(raw);
    if (ms === undefined) return { amount: raw, unit: "s" };
    if (ms === 0) return { amount: "0", unit: "s" };
    if (ms % 3600000 === 0) return { amount: String(ms / 3600000), unit: "h" };
    if (ms % 60000 === 0) return { amount: String(ms / 60000), unit: "m" };
    if (ms % 1000 === 0) return { amount: String(ms / 1000), unit: "s" };
    return { amount: String(ms), unit: "ms" };
  }

  private commit(amount: string, unit: string) {
    const trimmed = amount.trim();
    if (trimmed === "") {
      this.local = "";
      this.fire("");
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n)) {
      this.local = "Enter a number.";
      return;
    }
    const ms = n * unitMs(unit);
    if (ms < this.min) {
      this.local =
        this.min === 0 ? "A negative duration is rejected." : `At least ${this.min} milliseconds.`;
      return;
    }
    this.local = "";
    this.fire(ms === 0 ? "" : `${trimmed}${unit}`);
  }

  private fire(value: string) {
    this.value = value;
    this.dispatchEvent(
      new CustomEvent<DurationChangeDetail>("duration-change", {
        detail: { value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const { amount, unit } = this.parts;
    const message = this.local || this.diagnostic;
    return html`
      <bn-field
        label=${this.label}
        hint=${this.help}
        ?required=${this.required}
        error=${message}
        rule=${this.local ? "" : this.rule}
        severity=${this.local ? "error" : this.severity}
      >
        <div class="pair">
          <bn-input
            class="amount"
            type="number"
            inputmode="decimal"
            .value=${amount}
            ?disabled=${this.disabled}
            aria-label=${`${this.label} amount`}
            @input=${(e: CustomEvent<{ value: string }>) => {
              e.stopPropagation();
              this.commit(e.detail.value, unit);
            }}
          ></bn-input>
          <bn-select
            class="unit"
            .options=${DURATION_UNITS}
            .value=${unit}
            ?disabled=${this.disabled}
            aria-label=${`${this.label} unit`}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
              e.stopPropagation();
              this.commit(amount, e.detail.value);
            }}
          ></bn-select>
        </div>
      </bn-field>
    `;
  }
}

/** unitMs is one unit in milliseconds. */
function unitMs(unit: string): number {
  switch (unit) {
    case "ms":
      return 1;
    case "m":
      return 60000;
    case "h":
      return 3600000;
    default:
      return 1000;
  }
}

/**
 * durationToMs parses the three spellings the wire accepts, or undefined.
 *
 * A bare number means **seconds**, not milliseconds — `pkg/apitypes/duration.go` is
 * explicit — and reading `30` as 30ms would silently turn a thirty-second timeout
 * into an instant one.
 */
export function durationToMs(raw: string): number | undefined {
  const s = raw.trim();
  if (s === "") return 0;
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s) * 1000;
  const re = /(-?\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)/g;
  let total = 0;
  let matched = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    matched += m[0].length;
    const n = Number(m[1]);
    switch (m[2]) {
      case "ns":
        total += n / 1e6;
        break;
      case "us":
      case "µs":
        total += n / 1000;
        break;
      case "ms":
        total += n;
        break;
      case "s":
        total += n * 1000;
        break;
      case "m":
        total += n * 60000;
        break;
      default:
        total += n * 3600000;
    }
  }
  return matched === s.length ? total : undefined;
}

/* ── policy-secret-ref ─────────────────────────────────────────────────── */

/** Detail of policy-secret-ref's `secret-change` event. */
export interface SecretRefChangeDetail {
  value: string;
}

/**
 * `<policy-secret-ref>` picks a `secret://` reference out of the instance's
 * materialised inventory (`SC-F326`).
 *
 * It is a picker and never a text field, for two independent reasons. Sluice's
 * resolver accepts only `env` and `file` — `vault`, `aws-sm` and `gcp-sm` are
 * rejected at *parse*, which fails `config validate` and fails boot — and a
 * reference the control plane has not materialised resolves to nothing at query
 * time. A picker over the inventory cannot express either mistake.
 *
 * **Control-plane-owned secrets are not offered.** A mask `saltRef` or an API-key
 * `hashRef` pointed at the key pepper or at the rendered admin config is a hash
 * oracle, not a typo (doc 07 T-07), and linter rule `L31` refuses one that arrives
 * by any other route. The filter is the `cp.` name prefix the inventory reserves.
 *
 * The zero states are distinguished, as `SC-S519` requires: nothing has been
 * materialised for this instance yet, versus the inventory could not be read. A
 * value already on the object that the inventory does not carry stays visible and
 * unselectable, because that is exactly the state `L15` exists to catch and
 * blanking the field would hide it.
 *
 * Events: `secret-change` — `CustomEvent<SecretRefChangeDetail>`, composed.
 */
@customElement("policy-secret-ref")
export class PolicySecretRef extends LitElement {
  @property() instanceId = "";
  @property() value = "";
  @property() label = "";
  @property() help = "";
  @property({ type: Boolean }) required = false;
  @property({ type: Boolean }) disabled = false;
  @property() diagnostic = "";
  @property() rule = "";
  @property() severity: BnDiagnosticSeverity = "error";

  @state() private entries: SecretEntry[] = [];
  @state() private loading = false;
  @state() private failure: ApiError | null = null;

  static override styles = [kindFormStyles];

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  override updated(changed: PropertyValues<this>) {
    if (changed.has("instanceId")) void this.load();
  }

  private async load() {
    if (this.instanceId === "") return;
    this.loading = true;
    try {
      const page = await listSecrets(this.instanceId);
      this.entries = page.items.filter((s) => !s.name.startsWith("cp."));
      this.failure = null;
    } catch (err) {
      this.failure = parseError(err);
      this.entries = [];
    } finally {
      this.loading = false;
    }
  }

  private get options(): BnSelectOption[] {
    const out: BnSelectOption[] = [{ value: "", label: "None" }];
    for (const s of this.entries) {
      out.push({
        value: s.ref,
        label: s.name,
        description: s.used_by.length > 0 ? `${s.ref} · used by ${s.used_by.join(", ")}` : s.ref,
      });
    }
    if (this.value !== "" && !this.entries.some((s) => s.ref === this.value)) {
      out.push({
        value: this.value,
        label: this.value,
        disabled: true,
        disabledReason:
          "Not in this instance's secret inventory. Sluice resolves it to nothing, and the publish gate refuses it (L15).",
      });
    }
    return out;
  }

  override render() {
    const failure = this.failure;
    const hint = failure
      ? `The secret inventory could not be read (${failure.code}). ${failure.message}${failure.requestId ? ` Reference ${failure.requestId}.` : ""}`
      : this.entries.length === 0 && !this.loading
        ? "No secrets have been materialised for this instance yet. Add one on the instance's secrets screen, then pick it here."
        : this.help;
    return html`
      <bn-field
        label=${this.label}
        hint=${hint}
        ?required=${this.required}
        error=${this.diagnostic}
        rule=${this.rule}
        severity=${this.severity}
      >
        <bn-select
          filterable
          .options=${this.options}
          .value=${this.value}
          ?disabled=${this.disabled || this.loading}
          placeholder=${this.loading ? "Loading secrets…" : "None"}
          emptyText="No matching secret."
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            e.stopPropagation();
            this.value = e.detail.value;
            this.dispatchEvent(
              new CustomEvent<SecretRefChangeDetail>("secret-change", {
                detail: { value: e.detail.value },
                bubbles: true,
                composed: true,
              }),
            );
          }}
        ></bn-select>
      </bn-field>
    `;
  }
}

/* ── The two blocks every enforcement kind repeats ─────────────────────── */

/** What {@link renderMatchers} needs. `specGroup` is the kind's own spec group,
 * so the help text and the exclude explainer come from the schema. */
export interface MatcherBlockOptions {
  schema: PolicySchema;
  kind: string;
  specGroup: string;
  match: Selector;
  exclude: Selector | undefined;
  cache: SchemaCache;
  findings: PolicyFinding[];
  bindings: BindingSummary[];
  availableTags: string[];
  onMatch: (selector: Selector) => void;
  /** Called with `undefined` when the exclude selector empties, so the key leaves
   * the document instead of rendering as `exclude: {}` — which is accepted
   * upstream and permanently false. */
  onExclude: (selector: Selector | undefined) => void;
}

/**
 * renderMatchers renders the "who and what it applies to" and "exceptions"
 * sections that the seven enforcement kinds share.
 *
 * It exists so the exclude explainer is generated from **one** reading of the
 * schema's `excludeScope` rather than retyped per kind. That scope is the whole
 * point of the section: for five kinds an exclusion drops the policy from the
 * entire query, and for `RowFilterPolicy` and `ColumnMaskPolicy` it is evaluated
 * per table — so excluding one table never lifts protection from the others in a
 * join, and a subject-only exclude clause still drops the policy for every table.
 */
export function renderMatchers(o: MatcherBlockOptions): TemplateResult {
  const scope = o.schema.doc.kinds?.find((k) => k.kind === o.kind)?.excludeScope ?? "";
  const excludeHelp = o.schema.help(o.specGroup, "exclude");
  const perTable = scope === "per-table";
  return html`
    <section>
      <h3>Who and what it applies to</h3>
      <p class="lede">${o.schema.help(o.specGroup, "match")}</p>
      <policy-selector
        .schema=${o.schema}
        .selector=${o.match}
        path="spec.match"
        .cache=${o.cache}
        .findings=${o.findings}
        .bindings=${o.bindings}
        .availableTags=${o.availableTags}
        @selector-change=${(e: CustomEvent<{ selector: Selector }>) => {
          e.stopPropagation();
          o.onMatch(e.detail.selector);
        }}
      ></policy-selector>
    </section>

    <section>
      <h3>Exceptions</h3>
      <policy-selector
        is-exclude
        .schema=${o.schema}
        .selector=${o.exclude ?? {}}
        path="spec.exclude"
        .scopeExplainer=${excludeHelp ||
        (perTable
          ? "Evaluated per table, so excluding one table never lifts protection from the others in a join. A subject-only exclude clause still drops the policy for every table, because a subject-only clause matches any single-table context."
          : "If this matches anywhere in the query, the entire policy is dropped for that query.")}
        .cache=${o.cache}
        .findings=${o.findings}
        .bindings=${o.bindings}
        .availableTags=${o.availableTags}
        @selector-change=${(e: CustomEvent<{ selector: Selector }>) => {
          e.stopPropagation();
          const next = e.detail.selector;
          const empty = (next.any?.length ?? 0) === 0 && (next.all?.length ?? 0) === 0;
          o.onExclude(empty ? undefined : next);
        }}
      ></policy-selector>
    </section>
  `;
}

/**
 * renderConditions renders the CEL condition editor (`SC-F510`).
 *
 * It is a thin wrapper over `<policy-conditions>` rather than a second rendering,
 * because five kinds carry `spec.conditions` and a condition is the same hard gate
 * in all five: every one must be true for the policy to apply, and a runtime error
 * in one denies the entire request with `ACL_DENIED` rather than evaluating to
 * false. One component means the variable picker, the inline diagnostics and the
 * `request.*` refusal cannot be right on four editors and wrong on the fifth.
 *
 * `findings` is passed through so a finding the last save anchored to
 * `spec.conditions[i].expression` is drawn on that expression, next to whatever the
 * live check reports.
 */
export function renderConditions(
  schema: PolicySchema,
  conditions: Condition[] | undefined,
  onChange: (next: Condition[] | undefined) => void,
  findings: PolicyFinding[] = [],
): TemplateResult | typeof nothing {
  return html`<policy-conditions
    .schema=${schema}
    .conditions=${conditions ?? []}
    .findings=${findings}
    environment="condition"
    @conditions-change=${(e: CustomEvent<ConditionsChangeDetail>) => {
      e.stopPropagation();
      onChange(e.detail.conditions);
    }}
  ></policy-conditions>`;
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-duration": PolicyDuration;
    "policy-secret-ref": PolicySecretRef;
  }
}
